/**
 * Decodes a background image URL into an `HTMLImageElement`, which is what Agora's
 * `setOptions({ type: 'img', source })` requires — it will not take a URL.
 *
 * Cached by **URL, not background id**, so that if backgrounds ever arrive from the backend as
 * presigned URLs (which expire and get re-issued) we don't keep handing back an element built from a
 * dead URL — that would show up as "the background silently stops working after an hour".
 *
 * `crossOrigin` must be set before `src`. The processor uploads this image into a WebGL texture, and
 * a cross-origin image fetched without CORS taints it, making `texImage2D` throw a SecurityError.
 * See also the Chromium cache caveat in `common/utils/colors.ts`.
 */
const cache = new Map<string, Promise<HTMLImageElement>>();

export const loadBackgroundImage = (url: string): Promise<HTMLImageElement> => {
  const cached = cache.get(url);
  if (cached) return cached;

  const request = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load background image: ${url}`));
    image.src = url;
  }).catch((error: unknown) => {
    // A transient network blip shouldn't poison the cache for the rest of the session.
    cache.delete(url);
    throw error;
  });

  cache.set(url, request);
  return request;
};

/**
 * Drops one memoised image. Call this when its source is gone for good — a deleted custom background
 * — or the entry is retained for the rest of the session: a ~165K-character data URL as the key, plus
 * a decoded 1280x720 element the browser holds several megabytes of pixels for. Import and delete a
 * few images in one sitting and that adds up.
 */
export const evictBackgroundImage = (url: string) => {
  cache.delete(url);
};

/** Drops every memoised image. Also a test seam. */
export const clearBackgroundImageCache = () => {
  cache.clear();
};
